import { useState, type FormEvent } from 'react';
import { CornerDownRight, Pencil, Plus, Trash2 } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import {
  useCategories,
  useDeleteCategory,
  useSaveCategory,
  toCategoryTree,
  type AdminCategory,
} from '@/lib/catalog-api';
import { errorMessage, fieldErrors, formatNumber } from '@/lib/utils';

interface FormState {
  name: string;
  slug: string;
  parentId: string;
  description: string;
  displayOrder: string;
  isActive: boolean;
  isFeatured: boolean;
}

const EMPTY: FormState = {
  name: '',
  slug: '',
  parentId: '',
  description: '',
  displayOrder: '0',
  isActive: true,
  isFeatured: false,
};

/** The category tree (spec §12). */
export function CategoriesPage() {
  const categories = useCategories();
  const remove = useDeleteCategory();
  const [editing, setEditing] = useState<AdminCategory | null | undefined>(undefined);

  const rows = toCategoryTree(categories.data ?? []);

  return (
    <>
      <PageHeader
        title="Categories"
        description="Organise the catalogue. Products and subcategories must be moved before a category can be deleted."
        action={
          <Button size="sm" onClick={() => setEditing(null)}>
            <Plus className="size-4" aria-hidden="true" />
            Add Category
          </Button>
        }
      />

      {remove.isError && (
        <div className="mb-4">
          <ErrorAlert title="Could not delete that category">
            {errorMessage(remove.error)}
          </ErrorAlert>
        </div>
      )}

      <Card>
        {categories.isPending && (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-11 w-full" />
            ))}
          </div>
        )}

        {categories.isError && (
          <div className="p-4">
            <ErrorAlert title="Could not load categories">
              {errorMessage(categories.error)}
            </ErrorAlert>
          </div>
        )}

        {rows.length === 0 && !categories.isPending && (
          <div className="p-4">
            <EmptyState
              title="No categories yet"
              description="Create the first one to start filing products."
            />
          </div>
        )}

        {rows.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Category</Th>
                <Th>Slug</Th>
                <Th className="text-right">Products</Th>
                <Th className="text-right">Order</Th>
                <Th>Flags</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.map((category) => (
                <tr key={category.id} className="hover:bg-canvas">
                  <Td>
                    <div
                      className="flex items-center gap-1.5"
                      // Indentation carries the hierarchy the flat list encodes.
                      style={{ paddingLeft: `${category.depth * 20}px` }}
                    >
                      {category.depth > 0 && (
                        <CornerDownRight
                          className="size-3.5 shrink-0 text-ink-subtle"
                          aria-hidden="true"
                        />
                      )}
                      <span className="font-medium text-ink">{category.name}</span>
                    </div>
                  </Td>

                  <Td className="font-mono text-xs text-ink-subtle">{category.slug}</Td>

                  <Td className="text-right text-ink-muted">
                    {formatNumber(category.productCount)}
                    {category.childCount > 0 && (
                      <span className="ml-1 text-xs text-ink-subtle">
                        (+{category.childCount} sub)
                      </span>
                    )}
                  </Td>

                  <Td className="text-right text-ink-muted">{category.displayOrder}</Td>

                  <Td>
                    <div className="flex flex-wrap gap-1">
                      <Badge tone={category.isActive ? 'success' : 'neutral'}>
                        {category.isActive ? 'Active' : 'Hidden'}
                      </Badge>
                      {category.isFeatured && <Badge tone="accent">Featured</Badge>}
                    </div>
                  </Td>

                  <Td>
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(category)}
                        aria-label={`Edit ${category.name}`}
                        className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-canvas hover:text-brand-600"
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Delete "${category.name}"?`))
                            remove.mutate(category.id);
                        }}
                        aria-label={`Delete ${category.name}`}
                        className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-danger-50 hover:text-danger-600"
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <CategoryDrawer
        open={editing !== undefined}
        category={editing ?? null}
        categories={categories.data ?? []}
        onClose={() => setEditing(undefined)}
      />
    </>
  );
}

/**
 * Mounted only while open and keyed by category, so its state resets by
 * unmounting rather than by an effect syncing props into state.
 */
function CategoryDrawer({
  open,
  category,
  categories,
  onClose,
}: {
  open: boolean;
  category: AdminCategory | null;
  categories: AdminCategory[];
  onClose: () => void;
}) {
  if (!open) {
    return null;
  }

  return (
    <CategoryForm
      key={category?.id ?? 'new'}
      category={category}
      categories={categories}
      onClose={onClose}
    />
  );
}

function CategoryForm({
  category,
  categories,
  onClose,
}: {
  category: AdminCategory | null;
  categories: AdminCategory[];
  onClose: () => void;
}) {
  const save = useSaveCategory();
  const [form, setForm] = useState<FormState>(() =>
    category
      ? {
          name: category.name,
          slug: category.slug,
          parentId: category.parentId ?? '',
          description: category.description ?? '',
          displayOrder: String(category.displayOrder),
          isActive: category.isActive,
          isFeatured: category.isFeatured,
        }
      : EMPTY,
  );

  const errors = fieldErrors(save.error);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    save.mutate(
      {
        id: category?.id ?? null,
        body: {
          name: form.name,
          // Blank lets the server derive it from the name and make it unique.
          ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
          parentId: form.parentId || null,
          description: form.description || null,
          displayOrder: Number(form.displayOrder || 0),
          isActive: form.isActive,
          isFeatured: form.isFeatured,
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  // A category cannot be its own parent, and the server rejects deeper loops.
  const parentOptions = toCategoryTree(categories).filter((entry) => entry.id !== category?.id);

  return (
    <Drawer
      open
      onClose={onClose}
      title={category ? 'Edit category' : 'New category'}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="category-form" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <form id="category-form" onSubmit={handleSubmit} className="space-y-4">
        {save.isError && <ErrorAlert title="Could not save">{errorMessage(save.error)}</ErrorAlert>}

        <Field label="Name" htmlFor="category-name" required error={errors['name']}>
          <Input
            id="category-name"
            value={form.name}
            onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
            required
            minLength={2}
            invalid={Boolean(errors['name'])}
          />
        </Field>

        <Field
          label="Slug"
          htmlFor="category-slug"
          hint="Leave blank to generate from the name."
          error={errors['slug']}
        >
          <Input
            id="category-slug"
            value={form.slug}
            onChange={(event) => setForm((f) => ({ ...f, slug: event.target.value }))}
          />
        </Field>

        <Field label="Parent category" htmlFor="category-parent" error={errors['parentId']}>
          <Select
            id="category-parent"
            value={form.parentId}
            onChange={(event) => setForm((f) => ({ ...f, parentId: event.target.value }))}
          >
            <option value="">None — top level</option>
            {parentOptions.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {'  '.repeat(entry.depth)}
                {entry.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Description" htmlFor="category-description">
          <Textarea
            id="category-description"
            rows={3}
            value={form.description}
            onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))}
          />
        </Field>

        <Field label="Display order" htmlFor="category-order">
          <Input
            id="category-order"
            type="number"
            min={0}
            value={form.displayOrder}
            onChange={(event) => setForm((f) => ({ ...f, displayOrder: event.target.value }))}
          />
        </Field>

        <Checkbox
          label="Visible on the storefront"
          checked={form.isActive}
          onChange={(event) => setForm((f) => ({ ...f, isActive: event.target.checked }))}
        />
        <Checkbox
          label="Featured"
          checked={form.isFeatured}
          onChange={(event) => setForm((f) => ({ ...f, isFeatured: event.target.checked }))}
        />
      </form>
    </Drawer>
  );
}
